import {
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    LineController,
    LineElement,
    PointElement,
    Title,
    Tooltip,
} from 'chart.js'
import dayjs from 'dayjs'
import { t } from 'i18next'
import React, { useEffect, useRef } from 'react'
import { PriceVision } from '../../../models/bid/PriceVision'
import { PeriodType } from '../../business/MillBusiness'

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    LineController,
    Title,
    Tooltip,
    Legend
)

interface PriceChartProps {
    priceVisions: PriceVision[]
    selectedMillId?: number
    period: PeriodType
}

export const PriceChart: React.FC<PriceChartProps> = ({ priceVisions, selectedMillId, period }) => {

    const canvasRef = useRef<HTMLCanvasElement>(null)
    const chartInstanceRef = useRef<ChartJS | null>(null)

    useEffect(() => {
        if (priceVisions && priceVisions.length > 0) {
            createChart()
        }

        // Cleanup on unmount
        return () => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy()
            }
        }
    }, [priceVisions, period, selectedMillId])

    const generateTimeRangeData = (): { labels: string[], dataMap: Map<string, PriceVision[]> } => {
        const now = dayjs()
        const labels: string[] = []
        const dataMap = new Map<string, PriceVision[]>()

        // Filter original data by mill if provided
        let filteredData = priceVisions.filter(vision => vision.market_day)
        if (selectedMillId) {
            filteredData = filteredData.filter(vision => vision.mill_id === selectedMillId)
        }

        switch (period) {
            case 'week':
                // Generate last 7 days
                for (let i = 6; i >= 0; i--) {
                    const date = now.subtract(i, 'days')
                    const dateKey = date.format('YYYY-MM-DD')
                    const label = date.format('MMM DD')
                    labels.push(label)

                    // Find data for this specific date
                    const dayData = filteredData.filter(vision =>
                        dayjs(vision.market_day).utc().format('YYYY-MM-DD') === dateKey
                    )
                    dataMap.set(label, dayData)
                }
                break

            case 'month':
                // Generate last 30 days
                for (let i = 29; i >= 0; i--) {
                    const date = now.subtract(i, 'days')
                    const dateKey = date.format('YYYY-MM-DD')
                    const label = date.format('MMM DD')
                    labels.push(label)

                    const dayData = filteredData.filter(vision =>
                        dayjs(vision.market_day).utc().format('YYYY-MM-DD') === dateKey
                    )
                    dataMap.set(label, dayData)
                }
                break

            case 'year':
                // Generate last 12 months
                for (let i = 11; i >= 0; i--) {
                    const date = now.subtract(i, 'months')
                    const monthKey = date.format('YYYY-MM')
                    const label = date.format('MMM YYYY')
                    labels.push(label)

                    const monthData = filteredData.filter(vision =>
                        dayjs(vision.market_day).utc().format('YYYY-MM') === monthKey
                    )
                    dataMap.set(label, monthData)
                }
                break
        }

        return { labels, dataMap }
    }

    const createChart = () => {
        const canvas = canvasRef.current
        if (!canvas) return

        // Destroy existing chart
        if (chartInstanceRef.current) {
            chartInstanceRef.current.destroy()
        }

        const { labels, dataMap } = generateTimeRangeData()

        // Group data by mill if multiple mills in the dataset
        const millsPriceData = new Map<number, number[]>()
        const millsQuantityData = new Map<number, number[]>()

        labels.forEach((label, index) => {
            const dayData = dataMap.get(label) || []

            if (dayData.length === 0) {
                // No data for this time period - skip initialization of mill 0
                // Only add null values to existing mills
                millsPriceData.forEach((values, millId) => {
                    values[index] = null
                })
                millsQuantityData.forEach((values, millId) => {
                    values[index] = null
                })
            } else {
                // Process data for each mill - only process visions with valid mill_id
                dayData.forEach(vision => {
                    // Skip if no valid mill_id
                    if (!vision.mill_id) return

                    const millId = vision.mill_id
                    if (!millsPriceData.has(millId)) {
                        millsPriceData.set(millId, new Array(labels.length).fill(null))
                        millsQuantityData.set(millId, new Array(labels.length).fill(null))
                    }
                    const existingPrices = dayData.filter(v => v.mill_id === millId && v.price_per_kg)
                    const existingQuantities = dayData.filter(v => v.mill_id === millId && v.quantity)
                    // if (!existingPrices) return
                    const avgPrice = existingPrices?.reduce((acc, p) => acc + (p.price_per_kg ?? 0), 0) ?? null
                    const avgQuantity = existingQuantities?.reduce((acc, p) => acc + (p.quantity ?? 0), 0) ?? null
                    millsPriceData.get(millId)![index] = avgPrice ? Math.floor(avgPrice / existingPrices.length)  : null
                    millsQuantityData.get(millId)![index] = avgQuantity ? avgQuantity : null
                })
            }
        })

        const datasets = []

        // Price datasets (left Y-axis)
        Array.from(millsPriceData.entries()).forEach(([millId, data], index) => {
            const colors = [
                'rgb(255, 99, 132)',
                'rgb(54, 162, 235)',
                'rgb(255, 205, 86)',
                'rgb(75, 192, 192)',
                'rgb(153, 102, 255)'
            ]

            datasets.push({
                label: t('price'),
                data: data,
                borderColor: colors[index % colors.length],
                backgroundColor: colors[index % colors.length] + '20',
                tension: 0.4, // More curved lines
                spanGaps: true,
                yAxisID: 'y', // Left Y-axis for price
            })
        })

        // Quantity datasets (right Y-axis)
        Array.from(millsQuantityData.entries()).forEach(([millId, data], index) => {
            datasets.push({
                label: t('quantity'),
                data: data,
                borderColor: 'rgb(54, 162, 235)', // Solid blue line
                backgroundColor: 'rgb(54, 162, 235)' + '40',
                tension: 0.4, // More curved lines
                spanGaps: true,
                yAxisID: 'y1', // Right Y-axis for quantity
            })
        })

        const ctx = canvas.getContext('2d')
        if (!ctx) return

        chartInstanceRef.current = new ChartJS(ctx, {
            type: 'line',
            data: {
                labels: labels,
                datasets
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        position: 'top',
                    },
                },
                scales: {
                    y: {
                        type: 'linear',
                        display: true,
                        position: 'left',
                        title: {
                            display: true,
                            text: t('price')
                        }
                    },
                    y1: {
                        type: 'linear',
                        display: true,
                        position: 'right',
                        beginAtZero: true,
                        title: {
                            display: true,
                            text: t('quantity')
                        },
                        // so that the right y-axis line doesn't overlap with the dataset
                        grid: {
                            drawOnChartArea: false,
                        },
                    },
                    x: {
                        title: {
                            display: true,
                            text: t('date')
                        }
                    }
                },
            }
        })
    }

    return (
        <div style={{ width: '100%', marginTop: '20px' }}>
            <div className='pempem-flex-row-rev'>
                <div>
                    <h3 className='bold'>{t('price_history_title')}</h3>
                </div>
            </div>
            <div style={{ height: '400px' }}>
                <canvas ref={canvasRef} />
            </div>
        </div>
    )
}